import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LuChevronRight, LuContact, LuSearch } from 'react-icons/lu';
import { useTheme } from '../../../contexts/ThemeContext';
import useSchoolStore from '../../../store/schoolStore';
import useStaffStore from '../../../store/staffStore';
import { cleanStaff, getStaffStatus, getStaffDepartment, getStaffRole, formatPhoneNumber, formatDate, getUniqueDepartments } from '../../../utils/staffUtils';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import PageHeader from '../../../components/common/PageHeader';
import Table, { tdClass } from '../../../components/common/Table';
import Badge, { toneForStatus } from '../../../components/common/Badge';
import Avatar from '../../../components/common/Avatar';
import EmptyState from '../../../components/common/EmptyState';
import ErrorWidget from '../../../components/common/ErrorWidget';
import LoadingMessage from '../../../components/common/LoadingMessage';
import { SearchInput, Select } from '../../../components/common/FormControls';
import UnsupportedFeatureDialog from '../../../components/utils/UnsupportedFeatureDialog';
import Logger from '../../../utils/logger';

/**
 * All staff of the school, with a name search and a department filter.
 * A row opens the staff member's details. Data comes from useStaffStore.getStaffBySchool.
 */
const StaffManagementPage = () => {
  const navigate = useNavigate();
  const { schoolId: routeSchoolId } = useParams();
  const { baseColors, isDark } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);

  const { schoolId } = useSchoolStore();

  const {
    staff,
    isStaffLoading,
    staffError,
    getStaffBySchool
  } = useStaffStore();

  useEffect(() => {
    if (!schoolId) {
      Logger.warn('School ID not available. Please select a school first.');
      return;
    }

    const fetchData = async () => {
      try {
        await getStaffBySchool(schoolId);
      } catch (error) {
        Logger.error('Error fetching staff:', error);
      }
    };

    fetchData();
  }, [schoolId, getStaffBySchool]);

  const staffDetailsPath = (staffMember) => `/dash/${routeSchoolId}/staff/${staffMember.userId || staffMember.id}`;

  // Clean staff first, then filter based on search term and department
  const cleanedStaff = cleanStaff(staff);
  const filteredStaff = cleanedStaff.filter(staffMember => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        staffMember.name?.toLowerCase().includes(searchLower) ||
        staffMember.teacher?.gmail?.toLowerCase().includes(searchLower) ||
        getStaffRole(staffMember).toLowerCase().includes(searchLower) ||
        staffMember.userId?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    if (selectedDepartment !== 'all') {
      if (getStaffDepartment(staffMember) !== selectedDepartment) return false;
    }

    return true;
  });

  const departments = getUniqueDepartments(cleanedStaff);

  if (!schoolId) {
    return <EmptyState title="No school selected" message="Open the dashboard from your school’s link to see its staff." />;
  }

  const renderResults = () => {
    if (isStaffLoading) {
      return <LoadingMessage message="Loading staff…" />;
    }

    if (staffError) {
      return (
        <ErrorWidget
          error={{ message: staffError }}
          title="Could not load the staff"
          onRetry={() => getStaffBySchool(schoolId)}
          className="my-8 border-0 shadow-none"
        />
      );
    }

    if (cleanedStaff.length === 0) {
      return <EmptyState icon={<LuContact />} title="No staff yet" message="No staff members are listed for this school." />;
    }

    if (filteredStaff.length === 0) {
      return <EmptyState icon={<LuSearch />} title="No matching staff" message="Try another name or choose “All departments”." />;
    }

    return (
      <Table head={['Staff member', 'Role and department', 'Contact', 'Joined', 'Status', '']}>
        {filteredStaff.map((staffMember, index) => {
          const status = getStaffStatus(staffMember, baseColors, isDark);
          return (
            <tr key={staffMember.id || index} onClick={() => navigate(staffDetailsPath(staffMember))} className="cursor-pointer">
              <td className={tdClass}>
                <div className="flex items-center gap-3">
                  <Avatar name={staffMember.name} src={staffMember.profileUrl} />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{staffMember.name || '—'}</p>
                    {staffMember.teacher?.staffCode && (
                      <p className="text-xs text-ui-ink-3">Staff code {staffMember.teacher.staffCode}</p>
                    )}
                  </div>
                </div>
              </td>
              <td className={tdClass}>
                <p>{getStaffRole(staffMember)}</p>
                <p className="text-xs text-ui-ink-3">{getStaffDepartment(staffMember)}</p>
              </td>
              <td className={tdClass}>
                <p className="max-w-56 truncate">{staffMember.teacher?.gmail || '—'}</p>
                <p className="text-xs text-ui-ink-3">{formatPhoneNumber(staffMember.teacher?.mobNo)}</p>
              </td>
              <td className={`${tdClass} whitespace-nowrap`}>{formatDate(staffMember.teacher?.joinDate)}</td>
              <td className={tdClass}>
                <Badge tone={toneForStatus(status.label)}>{status.label}</Badge>
              </td>
              <td className={`${tdClass} w-24 text-right`}>
                <Link
                  to={staffDetailsPath(staffMember)}
                  onClick={(event) => event.stopPropagation()}
                  className="inline-flex items-center gap-1 rounded text-[13px] font-medium text-ui-link hover:underline"
                >
                  View
                  <LuChevronRight aria-hidden className="size-4" />
                </Link>
              </td>
            </tr>
          );
        })}
      </Table>
    );
  };

  const hasResults = !isStaffLoading && !staffError && cleanedStaff.length > 0;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Staff"
        subtitle="Everyone who works at the school, with their role and contact details."
        actions={
          <Button variant="secondary" onClick={() => setShowUnsupportedDialog(true)}>
            Add staff
          </Button>
        }
      />

      <Card
        noPadding
        title={hasResults ? `${filteredStaff.length} of ${cleanedStaff.length} staff members` : 'Staff members'}
        headerAction={
          hasResults && (
            <>
              <Select
                value={selectedDepartment}
                onChange={(event) => setSelectedDepartment(event.target.value)}
                aria-label="Department"
                className="w-full sm:w-48"
              >
                <option value="all">All departments</option>
                {departments.map(department => (
                  <option key={department} value={department}>{department}</option>
                ))}
                <option value="General">General</option>
              </Select>
              <SearchInput
                placeholder="Search by name, email or role"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full sm:w-64"
              />
            </>
          )
        }
      >
        {renderResults()}
      </Card>

      <UnsupportedFeatureDialog
        isOpen={showUnsupportedDialog}
        onClose={() => setShowUnsupportedDialog(false)}
        feature="Staff Management Actions"
      />
    </div>
  );
};

export default StaffManagementPage;
